import { useState, type FormEvent } from 'react';
import { siteInfo } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/dahcgaboh653ivfvnrbg';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

function NewsletterCta() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        String(serverMsg).toLowerCase().includes('spam') ||
        responseText.toLowerCase().includes('form data is spam');

      if (!response.ok || isSpam || (code && code !== 'OK')) {
        setStatus('error');
        setFormError(String(serverMsg) || '提交未成功，请稍后重试。');
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功，请稍后重试。');
    }
  };

  return (
    <section className="w-full px-4 pb-16 md:px-8 md:pb-24" aria-label="热点订阅">
      <div className="relative overflow-hidden rounded-lg bg-secondary-950 px-5 py-10 md:px-12 md:py-14">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-500/20" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-accent-500/20" />

        <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-3 py-1 text-xs text-primary-300">
              <i className="ri-notification-3-line" />
              每日热点摘要
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
              每天一封，把当天最值得追的瓜送到你邮箱
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-200/85">
              我们会在每天固定时段整理当天新增的快讯与专题进展，只保留还在发酵、值得继续跟的事件，不做无关推送。
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-xs text-background-300/80">
              <i className="ri-mail-line" />
              {siteInfo.updateFreq}
            </p>
          </div>

          <div className="rounded-lg bg-background-50 p-5 md:p-6">
            {status === 'success' ? (
              <div className="flex flex-col items-center py-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-700">
                  <i className="ri-checkbox-circle-line" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  订阅成功
                </h3>
                <p className="mt-2 text-sm text-foreground-600">
                  我们已收到你的订阅，下一期热点摘要会按时送达。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-5 cursor-pointer text-sm text-primary-700 hover:text-primary-800"
                >
                  再订阅一个邮箱
                </button>
              </div>
            ) : (
              <form data-readdy-form="true" id="chigua-subscribe-form" onSubmit={handleSubmit}>
                <label
                  htmlFor="sub-email"
                  className="block text-sm font-medium text-foreground-900"
                >
                  邮箱地址
                </label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />

                <label
                  htmlFor="sub-topic"
                  className="mt-4 block text-sm font-medium text-foreground-900"
                >
                  最想追踪的方向
                </label>
                <select
                  id="sub-topic"
                  name="interest"
                  className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="全部热点">全部热点</option>
                  <option value="影视">影视</option>
                  <option value="综艺">综艺</option>
                  <option value="音乐">音乐</option>
                  <option value="网络">网络</option>
                  <option value="品牌">品牌</option>
                  <option value="科技">科技</option>
                </select>

                <div className="form-honeypot-field" aria-hidden="true">
                  <label htmlFor="sub-website-alt">请勿填写此字段</label>
                  <input
                    id="sub-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === 'error' && (
                  <p className="mt-3 flex items-start gap-1.5 text-xs text-accent-700">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{formError}</span>
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      免费订阅热点摘要
                    </>
                  )}
                </button>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  订阅仅用于接收热点摘要，我们不会将你的邮箱用于其他用途。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default NewsletterCta;